import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getActiveAreas, getNearbyDeals } from '../services/apiService'
import './DetectPage.css'

// A gebied with nothing running in it is a dead end: the walker picks it,
// lands on the feed and finds an empty list. So every candidate is checked
// before it is offered. The checks run together — one round trip's worth of
// waiting, not one per gebied — and an area whose check fails is kept, since
// a flaky network should not make a winkelcentrum disappear.
async function areasWithDeals(areas) {
  const hasDeals = await Promise.all(
    areas.map(async (area) => {
      try {
        const deals = await getNearbyDeals(
          area.lat, area.lng, area.radius, { areaId: area.id }
        )
        return deals.length > 0
      } catch {
        return true
      }
    })
  )
  return areas.filter((_, i) => hasDeals[i])
}

// Used only as an offline fallback when the /areas endpoint is unreachable.
const FALLBACK_AREAS = [
  { name: 'Amsterdam Zuid AS', lat: 52.3367, lng: 4.8688, radius: 10000 },
  { name: 'Winkelcentrum Amsterdamse Poort', lat: 52.3139, lng: 4.9525, radius: 10000 },
  { name: 'Winkelcentrum Gelderlandplein', lat: 52.3389, lng: 4.8774, radius: 10000 },
  { name: 'Alexandrium Rotterdam', lat: 51.9490, lng: 4.5582, radius: 10000 },
  { name: 'Citymall Almere', lat: 52.3703, lng: 5.2141, radius: 10000 },
  { name: 'Shopping Center Hoge Schie', lat: 51.9225, lng: 4.4792, radius: 10000 },
  { name: 'Stadshart Lelystad', lat: 52.5088, lng: 5.4721, radius: 10000 },
]

function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
}

export default function DetectPage() {
  const navigate = useNavigate()
  // Where to go once the gebied is known: the feed by default, or the map
  // when detection was started from the "Kaart" button on the home page.
  const [searchParams] = useSearchParams()
  const target = searchParams.get('next') === 'map' ? '/map' : '/feed'
  const [status, setStatus] = useState('idle')
  const [debugInfo, setDebugInfo] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [nearbyAreas, setNearbyAreas] = useState([])
  // The list of WalkIn areas, loaded from the backend (with an offline
  // fallback). Held in a ref so startDetection() — also called by the
  // "Probeer opnieuw" buttons — always sees the latest list.
  const areasRef = useRef(FALLBACK_AREAS)

  function selectArea(area) {
    sessionStorage.setItem('walkin_area_name', area.name)
    // The gebied identity is what scopes the deal feed.
    if (area.id) sessionStorage.setItem('walkin_area_id', area.id)
    else sessionStorage.removeItem('walkin_area_id')
    sessionStorage.setItem('walkin_location_status', 'active')
    // Store the area's coordinates so the feed can query nearby deals from the
    // API even when the walker picked the area manually (no GPS fix).
    sessionStorage.setItem('walkin_user_lat', String(area.lat))
    sessionStorage.setItem('walkin_user_lng', String(area.lng))
    // The area's own radius drives how far the feed looks for deals; without
    // this every query fell back to the 10 km default.
    sessionStorage.setItem('walkin_area_radius', String(area.radius || ''))
    sessionStorage.removeItem('walkin_demo_mode')
    navigate(target)
  }

  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        // getActiveAreas drops areas switched off in the admin panel, so a
        // deactivated winkelcentrum stops being detectable or selectable.
        const apiAreas = await getActiveAreas()
        if (!cancelled && Array.isArray(apiAreas) && apiAreas.length > 0) {
          // Coerced explicitly: the API returns numbers today, but Nominatim
          // hands back lat/lon as strings and the admin form writes whatever
          // its inputs hold, so an area saved that way must not silently break
          // the distance comparison. Areas without usable coordinates are
          // dropped rather than compared against NaN.
          const usable = apiAreas
            .map((a) => ({
              id: a.id,
              name: a.name,
              lat: Number(a.lat),
              lng: Number(a.lng),
              radius: Number(a.radiusM ?? a.radius) || 10000,
            }))
            .filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lng))
          // Keep the offline fallback if nothing usable came back, rather than
          // detecting against an empty list.
          if (usable.length > 0) areasRef.current = usable
        }
      } catch {
        /* keep FALLBACK_AREAS */
      }
      if (!cancelled) startDetection()
    }
    init()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function startDetection() {
    setStatus('detecting')
    setDebugInfo(null)
    console.log('[WalkIn] startDetection invoked, calling navigator.geolocation.getCurrentPosition')

    if (!navigator.geolocation) {
      console.log('[WalkIn] navigator.geolocation is NOT available')
      setStatus('unavailable')
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords
        console.log('GPS SUCCESS:', latitude, longitude)
        console.log('Accuracy:', accuracy)

        sessionStorage.setItem('walkin_user_lat', latitude.toString())
        sessionStorage.setItem('walkin_user_lng', longitude.toString())

        const areas = areasRef.current

        // Find all nearby areas
        const nearby = areas.map(area => ({
          ...area,
          distance: Math.round(calculateDistance(
            latitude, longitude, area.lat, area.lng
          ))
        }))
        .filter(area => area.distance <= area.radius)
        .sort((a, b) => a.distance - b.distance)

        console.log('Nearby areas found:', nearby.length)
        nearby.forEach(a => console.log(a.name, a.distance))

        // Debug info
        const allDistances = areas.map(area => ({
          name: area.name,
          distance: Math.round(calculateDistance(
            latitude, longitude, area.lat, area.lng
          )),
          radius: area.radius
        })).sort((a, b) => a.distance - b.distance)

        setDebugInfo({
          lat: latitude.toFixed(4),
          lng: longitude.toFixed(4),
          accuracy: Math.round(accuracy),
          areas: allDistances
        })

        // 'Locatie bepalen...' stays up while this runs — the walker is
        // already looking at a loading screen, so there is nothing to add.
        const stocked = await areasWithDeals(nearby)
        // Falling back to the unfiltered list keeps the picker from going
        // blank when nothing anywhere has deals. One detected gebied with
        // nothing in it is a dead end though, not a reason to fall back.
        const shown = stocked.length > 0 ? stocked : nearby

        if (nearby.length === 1 && stocked.length === 0) {
          setStatus('not_found')
        } else if (shown.length > 1) {
          // Multiple areas — let the user pick
          setNearbyAreas(shown)
          setStatus('multiple_found')
        } else if (shown.length === 1) {
          // Single area — auto-navigate. walkin_user_lat/lng are already set
          // from the GPS fix above; the radius comes from the matched area.
          sessionStorage.setItem(
            'walkin_area_name', shown[0].name
          )
          if (shown[0].id) sessionStorage.setItem('walkin_area_id', shown[0].id)
          else sessionStorage.removeItem('walkin_area_id')
          sessionStorage.setItem(
            'walkin_location_status', 'active'
          )
          sessionStorage.setItem(
            'walkin_area_radius', String(shown[0].radius || '')
          )
          setStatus('found')
          setTimeout(() => navigate(target), 1500)
        } else {
          setStatus('not_found')
        }
      },
      (error) => {
        console.log('GPS ERROR code:', error.code)
        console.log('GPS ERROR message:', error.message)
        if (error.code === 1) {
          setStatus('denied')
        } else if (error.code === 2) {
          setStatus('unavailable')
        } else {
          setStatus('timeout')
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    )
  }

  return (
    <div className="detect">
      <div className="detect__safe-top" />

      <button
        className="detect__back"
        onClick={() => navigate('/')}
        aria-label="Terug naar start"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
      </button>

      {/* DETECTING */}
      {status === 'idle' || status === 'detecting' ? (
        <div className="detect__body">
          <div className="detect__pulse">
            <div className="detect__ring" />
            <div className="detect__ring detect__ring--2" />
            <div className="detect__ring detect__ring--3" />
            <div className="detect__center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
              </svg>
            </div>
          </div>
          <h2 className="detect__title">
            Je bent in de buurt...
          </h2>
          <p className="detect__sub">
            WalkIn zoekt winkels en deals
            in jouw omgeving
          </p>
          <div className="detect__status-pill">
            Locatie bepalen...
          </div>
        </div>
      ) : null}

      {/* MULTIPLE FOUND */}
      {status === 'multiple_found' ? (
        <div className="detect__body">
          <h2 className="detect__title">
            Meerdere gebieden gevonden!
          </h2>
          <p className="detect__sub">
            Kies een winkelgebied om deals te bekijken
          </p>

          <div className="detect__area-list">
            {nearbyAreas.map(area => (
              <button
                key={area.name}
                className="detect__area-card"
                onClick={() => selectArea(area)}
              >
                <div className="detect__area-info">
                  <div className="detect__area-name">
                    {area.name}
                  </div>
                  <div className="detect__area-distance">
                    {area.distance < 1000
                      ? area.distance + 'm'
                      : (area.distance / 1000).toFixed(1) + 'km'} afstand
                  </div>
                </div>
                <svg className="detect__area-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* FOUND */}
      {status === 'found' ? (
        <div className="detect__body">
          <h2 className="detect__title">Gevonden!</h2>
          <div className="detect__found-pill">
            {sessionStorage.getItem('walkin_area_name')}
          </div>
          <p className="detect__sub">
            Deals worden geladen...
          </p>
        </div>
      ) : null}

      {/* NOT FOUND */}
      {status === 'not_found' ? (
        <div className="detect__body">
          <h2 className="detect__title">
            Geen WalkIn gebied
          </h2>
          <p className="detect__sub">
            Je bent momenteel niet in de buurt
            van een deelnemende winkelstraat.
          </p>

          {debugInfo && (
            <div className="detect__debug">
              <p>{debugInfo.lat}, {debugInfo.lng}
                (±{debugInfo.accuracy}m)
              </p>
              {debugInfo.areas.slice(0,3).map(a => (
                <p key={a.name}>
                  {a.name}: {a.distance}m
                  (radius: {a.radius}m)
                  {a.distance <= a.radius ? ' — in bereik' : ''}
                </p>
              ))}
            </div>
          )}

          <p className="detect__no-area-text">
            Geen WalkIn gebied gevonden in jouw buurt
          </p>
          <button
            className="detect__btn-primary"
            onClick={startDetection}>
            Probeer opnieuw
          </button>
        </div>
      ) : null}

      {/* DENIED */}
      {status === 'denied' ? (
        <div className="detect__body">
          <div className="detect__denied-pill">
            Geweigerd
          </div>
          <h2 className="detect__title">
            Locatie toegang vereist
          </h2>
          <p className="detect__sub">
            Sta locatietoegang toe in je
            browserinstellingen om WalkIn deals
            in de buurt te ontdekken.
          </p>

          <button
            className="detect__btn-primary"
            onClick={() => setShowModal(true)}>
            Open Instellingen
          </button>
          <button
            className="detect__btn-secondary"
            onClick={startDetection}>
            Probeer opnieuw
          </button>
          <button
            className="detect__btn-text"
            onClick={() => navigate('/feed')}>
            Blader alle deals
          </button>
        </div>
      ) : null}

      {/* UNAVAILABLE */}
      {status === 'unavailable' ? (
        <div className="detect__body">
          <h2 className="detect__title">
            Locatie niet beschikbaar
          </h2>
          <p className="detect__sub">
            GPS signaal te zwak. Ga naar buiten
            of schakel WiFi in.
          </p>
          <button
            className="detect__btn-primary"
            onClick={startDetection}>
            Probeer opnieuw
          </button>
        </div>
      ) : null}

      {/* TIMEOUT */}
      {status === 'timeout' ? (
        <div className="detect__body">
          <h2 className="detect__title">
            Locatie duurt te lang
          </h2>
          <p className="detect__sub">
            GPS neemt meer tijd dan verwacht.
            Probeer opnieuw.
          </p>
          <button
            className="detect__btn-primary"
            onClick={startDetection}>
            Probeer opnieuw
          </button>
        </div>
      ) : null}

      {/* SETTINGS MODAL */}
      {showModal && (
        <div className="detect__modal"
          onClick={() => setShowModal(false)}>
          <div className="detect__modal-card"
            onClick={e => e.stopPropagation()}>
            <h3>Locatie inschakelen op iPhone</h3>
            <p>Volg deze stappen:</p>
            <div className="detect__modal-steps">
              {[
                'Sluit WalkIn en open Instellingen',
                'Tik op Privacy en beveiliging',
                'Tik op Locatievoorzieningen',
                'Scroll naar Safari en tik erop',
                'Kies "Bij gebruik van app"',
                'Kom terug naar WalkIn'
              ].map((step, i) => (
                <div key={i}
                  className="detect__modal-step">
                  <span className="step-num">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <button
              className="detect__modal-btn"
              onClick={() => {
                setShowModal(false)
                startDetection()
              }}>
              Begrepen — Probeer opnieuw
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
